import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { BookOpen, ChevronRight, PencilRuler } from "lucide-react";
import { api, getStudentId } from "@/lib/api";
import Navbar from "@/components/Navbar";
import VoicePromptBanner from "@/components/VoicePromptBanner";
import { useVoicePrompt } from "@/hooks/use-voice-prompt";

export default function CurriculumBrowser() {
  const navigate = useNavigate();
  const studentId = getStudentId();
  const [student, setStudent] = useState(null);
  const [board, setBoard] = useState("CBSE");
  const [standard, setStandard] = useState("6");
  const [tree, setTree] = useState([]);
  const [activeChapter, setActiveChapter] = useState(null);
  const [masteryMap, setMasteryMap] = useState({});

  const { blocked: voiceBlocked, retry: retryVoice } = useVoicePrompt(
    "Browse through your subjects and chapters here.",
    "curriculum-intro"
  );

  useEffect(() => {
    api.get(`/students/${studentId}`).then(({ data }) => { setStudent(data); setBoard(data.board); setStandard(String(data.standard)); });
    api.get(`/mastery/${studentId}`).then(({ data }) => {
      const map = {};
      data.forEach((m) => (map[m.concept_id] = m.mastery_score));
      setMasteryMap(map);
    });
  }, [studentId]);

  useEffect(() => {
    if (!student) return;
    api.get("/curriculum/tree", { params: { board, standard } }).then(({ data }) => setTree(data));
  }, [student, board, standard]);

  return (
    <div className="min-h-screen bg-background">
      <Navbar student={student} />
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
        <h1 className="text-2xl font-heading font-extrabold mb-2">Curriculum Browser</h1>
        <VoicePromptBanner blocked={voiceBlocked} onRetry={retryVoice} testId="curriculum-voice-unlock-banner" />
        <p className="text-sm text-muted-foreground mb-6">
          <span className="inline-flex items-center gap-1.5 bg-primary/10 text-primary px-3 py-1 rounded-full text-xs font-semibold" data-testid="curriculum-scope-badge">CBSE · Class {standard}</span>
          <span className="ml-2">More classes and boards coming soon.</span>
        </p>

        {tree.length === 0 && (
          <p className="text-sm text-muted-foreground" data-testid="curriculum-empty-state">No curriculum content seeded yet for Class {standard} {board}.</p>
        )}

        {tree.map((subject) => (
          <div key={subject.id} className="mb-8">
            <h2 className="text-sm font-bold uppercase tracking-wider text-primary mb-3">{subject.name}</h2>
            {subject.chapters.map((chapter) => (
              <div key={chapter.id} className="mb-2 bg-card border border-border rounded-xl overflow-hidden">
                <button data-testid={`browser-chapter-${chapter.id}`} onClick={() => setActiveChapter(activeChapter === chapter.id ? null : chapter.id)}
                  className="w-full flex items-center justify-between px-4 py-3 hover:bg-muted">
                  <span className="flex items-center gap-2 font-semibold text-sm"><BookOpen size={15} className="text-muted-foreground" />{chapter.name}</span>
                  <ChevronRight size={16} className={`transition-transform ${activeChapter === chapter.id ? "rotate-90" : ""}`} />
                </button>
                {activeChapter === chapter.id && (
                  <div className="px-4 pb-3 space-y-2">
                    {chapter.concepts.map((concept) => (
                      <div key={concept.id} className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 border-t border-border pt-2" data-testid={`browser-concept-${concept.id}`}>
                        <div className="min-w-0">
                          <p className="text-sm font-medium truncate">{concept.name}</p>
                          {masteryMap[concept.id] !== undefined && (
                            <p className="text-xs text-muted-foreground">{Math.round(masteryMap[concept.id])}% mastery</p>
                          )}
                        </div>
                        <div className="flex gap-3 shrink-0">
                          <button data-testid={`browser-learn-${concept.id}`} onClick={() => navigate(`/tutor/learn/${concept.id}`)}
                            className="text-xs font-semibold text-primary flex items-center gap-1"><BookOpen size={12} /> Learn</button>
                          <button data-testid={`browser-practice-${concept.id}`} onClick={() => navigate(`/practice/${concept.id}`)}
                            className="text-xs font-semibold text-success flex items-center gap-1"><PencilRuler size={12} /> Practice</button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            ))}
          </div>
        ))}
      </div>
    </div>
  );
}
